import i18n from '@/i18n';
/**
 * 商品库主图视频：本地 meta 持久化 + 按目标平台校验格式。
 * TEMU：≤20MB，MP4/MOV/M4V/WebM，比例 1:1 / 3:4 / 16:9
 * SHEIN：仅 MP4，≤100MB
 */

import { normalizePlatformCode } from '@/utils/platformShopCatalog';
import {
  PUBLISH_VIDEO_ACCEPT,
  assertTemuPublishVideoRatio,
  isAllowedPublishVideoFormat,
  probeLocalVideoSize,
} from '@/pages/ipe/workbench/utils/videoSpaceLibraryPick';
import { getProductMeta, setProductMeta } from '@/pages/ipe/workbench/utils/platformListingProductMeta';

export const TEMU_LISTING_VIDEO_MAX_BYTES = 20 * 1024 * 1024;
export const SHEIN_LISTING_VIDEO_MAX_BYTES = 100 * 1024 * 1024;

export const TEMU_LISTING_VIDEO_ACCEPT = PUBLISH_VIDEO_ACCEPT;
export const SHEIN_LISTING_VIDEO_ACCEPT = 'video/mp4,.mp4';

function asText(value) {
  return String(value || '').trim();
}

export function resolveListingTargetPlatform(item, shops = []) {
  const meta = item?.id != null ? getProductMeta(item.id) : {};
  const shopId = asText(item?.targetShopId || meta.listingShopId);
  if (shopId) {
    const shop = (Array.isArray(shops) ? shops : []).find((row) => String(row?.id) === shopId);
    const fromShop = normalizePlatformCode(shop?.platformCode);
    if (fromShop) return fromShop;
  }
  const fromMeta = normalizePlatformCode(meta.listingPlatformCode || item?.platformCode);
  return fromMeta || '';
}

export function listingVideoAcceptForPlatform(platformCode) {
  const code = normalizePlatformCode(platformCode);
  if (code === 'SHEIN') return SHEIN_LISTING_VIDEO_ACCEPT;
  if (code === 'TEMU') return TEMU_LISTING_VIDEO_ACCEPT;
  // 未绑定店铺：放宽到 Temu 集合，上传时仍按实际平台再校验
  return TEMU_LISTING_VIDEO_ACCEPT;
}

export function listingVideoHintForPlatform(platformCode) {
  const code = normalizePlatformCode(platformCode);
  if (code === 'SHEIN') return i18n.t('workbench:listingVideo.hintShein');
  if (code === 'TEMU') return i18n.t('workbench:listingVideo.hintTemu');
  return i18n.t('workbench:listingVideo.hintDefault');
}

/**
 * @param {File} file
 * @param {string} platformCode
 */
export async function assertListingVideoForPlatform(file, platformCode) {
  if (!file) throw new Error(i18n.t('workbench:listingVideo.selectFile'));
  const code = normalizePlatformCode(platformCode);
  const name = asText(file.name).toLowerCase();
  const mime = asText(file.type).toLowerCase();
  const looksVideo = mime.startsWith('video/') || /\.(mp4|mov|m4v|webm|avi)$/i.test(name);
  if (!looksVideo) {
    throw new Error(i18n.t('workbench:listingVideo.selectFile'));
  }

  if (code === 'SHEIN') {
    const isMp4 = mime === 'video/mp4' || name.endsWith('.mp4');
    if (!isMp4) {
      throw new Error(i18n.t('workbench:listingVideo.sheinMp4Only'));
    }
    if (file.size > SHEIN_LISTING_VIDEO_MAX_BYTES) {
      throw new Error(i18n.t('workbench:listingVideo.sheinMax100'));
    }
    return { width: 0, height: 0 };
  }

  // TEMU 或未指定平台：按 Temu 规则（更严）
  if (file.size > TEMU_LISTING_VIDEO_MAX_BYTES) {
    throw new Error(i18n.t('workbench:listingVideo.temuMax20'));
  }
  const allowedExt = /\.(mp4|mov|m4v|webm)$/i.test(name)
    || /^(video\/mp4|video\/quicktime|video\/webm|video\/x-m4v)$/i.test(mime);
  if (!allowedExt) {
    throw new Error(i18n.t('workbench:listingVideo.temuFormats'));
  }
  return assertTemuPublishVideoRatio(file);
}

export function normalizeListingProductVideo(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const url = asText(
    raw.url
    || raw.ipeUrl
    || raw.outputVideoUrl
    || raw.videoUrl
    || raw.playUrl
    || raw.src,
  );
  if (!url) return null;
  return {
    url,
    ipeUrl: asText(raw.ipeUrl) || url,
    videoUrl: asText(raw.videoUrl),
    vid: asText(raw.vid),
    coverUrl: asText(raw.coverUrl || raw.posterUrl || raw.thumbnailUrl || raw._posterUrl),
    posterUrl: asText(raw.posterUrl || raw.coverUrl || raw._posterUrl || raw.thumbnailUrl),
    outputVideoPosterUrl: asText(raw.outputVideoPosterUrl),
    previewUrl: asText(raw.previewUrl),
    name: asText(raw.name || raw.fileName) || '商品视频',
    videoSpaceId: raw.videoSpaceId != null ? String(raw.videoSpaceId) : '',
    platformCode: normalizePlatformCode(raw.platformCode) || '',
    shopId: raw.shopId != null ? String(raw.shopId) : '',
    taskId: asText(raw.taskId),
    sheinUrl: asText(raw.sheinUrl),
  };
}

export function getListingProductVideo(item) {
  if (!item) return null;
  const fromItem = normalizeListingProductVideo(item.preferredProductVideo);
  if (fromItem) return fromItem;
  if (item.id == null) return null;
  const meta = getProductMeta(item.id);
  if (!meta) return null;
  return normalizeListingProductVideo(meta.preferredProductVideo);
}

export function setListingProductVideo(item, video) {
  if (!item?.id) throw new Error(i18n.t('workbench:listingVideo.missingProduct'));
  const next = normalizeListingProductVideo(video);
  setProductMeta(item.id, {
    preferredProductVideo: next || null,
    preferredSource: 'manual-edit',
  });
  return next;
}

export function clearListingProductVideo(item) {
  if (!item?.id) return;
  setProductMeta(item.id, {
    preferredProductVideo: null,
    preferredSource: 'manual-edit',
  });
}

export function listingProductVideoFromSpaceRow(row, platformCode = '') {
  if (!row || !isAllowedPublishVideoFormat(row)) {
    throw new Error(i18n.t('workbench:listingVideo.notUsableVideo'));
  }
  const playUrl = asText(
    row._playUrl
    || row.outputVideoUrl
    || row.videoUrl
    || row.url,
  );
  if (!playUrl) throw new Error(i18n.t('workbench:listingVideo.videoNoUrl'));
  return normalizeListingProductVideo({
    url: playUrl,
    ipeUrl: playUrl,
    coverUrl: row._posterUrl || row.posterUrl || row.coverUrl || row.thumbnailUrl || row.outputVideoPosterUrl,
    posterUrl: row._posterUrl || row.posterUrl || row.outputVideoPosterUrl || row.previewUrl,
    outputVideoPosterUrl: row.outputVideoPosterUrl,
    previewUrl: row.previewUrl,
    name: row.name || row.fileName || '商品视频',
    videoSpaceId: row.id,
    platformCode,
  });
}

export async function probeListingVideoFileMeta(file) {
  try {
    return await probeLocalVideoSize(file);
  } catch {
    return { width: 0, height: 0 };
  }
}
